import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK = [
  "Quanto costa un sito?",
  "Come funziona l'abbonamento SEO?",
  "Vorrei essere ricontattato",
];

function getSessionId() {
  let sid = localStorage.getItem("lumora_chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("lumora_chat_sid", sid);
  }
  return sid;
}

const WELCOME = {
  role: "assistant",
  text: "Ciao! Sono l'assistente virtuale di Lumora, attivo 24/7. Chiedimi pure di piani, prezzi, SEO o gestione del sito.",
};

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    fetch(`${API}/api/chat/history/${getSessionId()}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((docs) => {
        if (Array.isArray(docs) && docs.length)
          setMessages(docs.map((d) => ({ role: d.role, text: d.text })));
      })
      .catch(() => {});
  }, [open, loaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (preset) => {
    const message = (preset ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const payload = ev.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const token = JSON.parse(payload);
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + token };
              return copy;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Errore di connessione. Riprova tra poco o scrivici a lumoratech.it@gmail.com",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="support-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] rounded-2xl bg-white border border-black/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden flex flex-col"
          >
            <div className="bg-[#1E293B] text-white px-5 py-4 flex items-center justify-between">
              <div>
                <p className="font-display font-bold text-sm">Assistenza Lumora</p>
                <p className="text-xs text-neutral-300 mt-0.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online · risponde in pochi secondi, H24
                </p>
              </div>
              <button
                data-testid="support-chat-close"
                onClick={() => setOpen(false)}
                className="p-1.5 text-neutral-300 hover:text-white transition-colors"
                aria-label="Chiudi chat"
              >
                <X size={18} />
              </button>
            </div>

            <div data-testid="support-chat-messages" className="h-80 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAF8F5]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#111111] text-white rounded-br-md"
                        : "bg-white border border-black/10 text-neutral-800 rounded-bl-md"
                    }`}
                  >
                    {m.text.replace(/\*\*/g, "")}
                    {m.role === "assistant" && streaming && i === messages.length - 1 && (
                      <span className="inline-block w-1.5 h-3.5 bg-[#E05638] ml-0.5 animate-pulse" />
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length === 1 && (
              <div className="px-4 pb-2 bg-[#FAF8F5] flex flex-wrap gap-2">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    data-testid={`support-chat-quick-${QUICK.indexOf(q)}`}
                    onClick={() => send(q)}
                    className="text-xs rounded-full border border-black/15 bg-white px-3 py-1.5 text-neutral-600 hover:border-[#E05638] hover:text-[#E05638] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-black/10 bg-white p-3 flex items-center gap-2"
            >
              <input
                data-testid="support-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio…"
                className="flex-1 rounded-full border border-black/15 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
              />
              <button
                data-testid="support-chat-send"
                type="submit"
                disabled={!input.trim() || streaming}
                className="w-10 h-10 rounded-full bg-[#E05638] text-white grid place-items-center hover:bg-[#C8452A] transition-colors disabled:opacity-50"
                aria-label="Invia"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="support-chat-button"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-4 sm:right-6 z-50 rounded-full bg-[#111111] text-white pl-5 pr-6 py-3.5 flex items-center gap-2.5 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.4)] hover:bg-[#E05638] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2"
      >
        <MessageCircle size={18} />
        <span className="text-sm font-semibold">Assistenza H24</span>
      </button>
    </>
  );
}
